import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { destinationTeamCreateSchema } from "@ckgo2/shared/schemas";
import { TEAM_SECTION_TYPES } from "@ckgo2/shared/types";
import type { TeamSectionType } from "@ckgo2/shared/types";
import MediaPicker from "@/components/common/MediaPicker";
import DestinationPicker from "@/components/common/DestinationPicker";
import EditPageHeader from "@/components/common/EditPageHeader";
import { GuideProfileField } from "@/components/people/GuideProfileField";
import { TeamDestinationRows } from "@/components/people/TeamDestinationRows";
import { ADMIN_TEAM_LIST_KEY, useAdminTeamList, useGuideLookup } from "@/components/people/queries";
import {
  cardFieldsFromLinks,
  linksForIds,
  othersAtDestination,
  teamBodyForSave,
  type AdminTeamDetail,
  type TeamFormLink,
  type TeamFormValues,
} from "@/components/people/teamDestinations";
import { useEditPage } from "@/hooks/useEditPage";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { Plus, X } from "lucide-react";
import i18n from "@/i18n";

/**
 * ZRCADLO sdíleného výčtu typů sekcí. Typ `Record<TeamSectionType, string>` je tu
 * schválně: dokud to byl `Record<string, string>` čtený jako `MAPA[x] || x`,
 * vykreslil nový typ syrové `flight_booking` a nikdo se to nedozvěděl. Teď chybějící
 * položka spadne v `pnpm typecheck` (ten `apps/admin` pokrývá).
 */
const SECTION_LABELS: Record<TeamSectionType, string> = {
  guarantor: i18n.t("catalog.teams.sections.guarantor"),
  sales_team: i18n.t("catalog.teams.sections.salesTeam"),
  guide: i18n.t("catalog.teams.sections.guide"),
  local_team: i18n.t("catalog.teams.sections.localTeam"),
  flight_booking: i18n.t("catalog.teams.sections.flightBooking"),
  management: i18n.t("catalog.teams.sections.management"),
  branch_manager: i18n.t("catalog.teams.sections.branchManager"),
};

export default function DestinationTeamEditPage() {
  const { t } = useTranslation();
  const { id, isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    TeamFormValues,
    AdminTeamDetail
  >({
    resourceKey: "destination-team",
    listKey: ADMIN_TEAM_LIST_KEY[0],
    apiPath: "/api/admin/destination-teams",
    schema: destinationTeamCreateSchema,
    defaultValues: {
      destinationId: null,
      destinationLinks: [],
      sectionType: TEAM_SECTION_TYPES[0],
      name: "",
      subtitle: "",
      bio: "",
      email: "",
      phone: "",
      photo: "",
      photoMediaId: null,
      photos: [],
      photoIds: [],
      members: [],
      guideId: null,
      sortOrder: 0,
      isActive: true,
    },
    // Role a pořadí karty = první destinace; `guideId` "" → null.
    transformOnSave: teamBodyForSave,
    navigateTo: "/destination-teams",
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  // Jména řádků destinací (výběr sám si seznam načítá přes týž sdílený dotaz).
  const { data: destinationsData } = useAdminDestinationList();
  const destinations = destinationsData?.data || [];
  const destinationName = (destId: string) =>
    destinations.find((d) => d.id === destId)?.name ?? destId;

  // Nápověda „U destinace dále" čte seznam karet (sdílená keš se seznamem, po uložení se
  // obnoví přes `listKey`); profil průvodce čte všechny průvodce včetně neaktivních.
  const teamList = useAdminTeamList();
  const guideLookup = useGuideLookup();
  const others = (destId: string) =>
    teamList.data
      ? othersAtDestination(teamList.data.data ?? [], destId, isNew ? null : (id ?? null))
      : undefined;

  const members = watch("members") || [];
  const cardPhone = watch("phone");
  // Řádky destinací na úrovni stránky: čte je výběr, řádky i podmíněná pole karty níž.
  const links = watch("destinationLinks");
  const cardSectionType = watch("sectionType");
  // `valueAsNumber` dá u prázdného pole NaN; nový první řádek by ho převzal a neprošel schématem.
  const cardSortOrder = Number.isFinite(watch("sortOrder")) ? watch("sortOrder") : 0;

  /**
   * Nové řádky do formuláře a role a pořadí první destinace živě do polí karty. Po odebrání
   * poslední destinace v polích karty zůstanou její hodnoty. (Jméno bez „destination":
   * měřidlo `admin-editory`, pravidlo `nativni-vyber-destinace`.)
   */
  const applyLinks = (next: TeamFormLink[]) => {
    setValue("destinationLinks", next, { shouldDirty: true });
    const card = cardFieldsFromLinks(next);
    if (card) {
      setValue("sectionType", card.sectionType, { shouldDirty: true });
      setValue("sortOrder", card.sortOrder, { shouldDirty: true });
    }
  };

  const onRowChange = (index: number, patch: Partial<TeamFormLink>) =>
    applyLinks(links.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const addMember = () => {
    setValue("members", [...members, { name: "", role: "", photoId: null }], { shouldDirty: true });
  };

  const removeMember = (index: number) => {
    setValue(
      "members",
      members.filter((_, i) => i !== index),
      { shouldDirty: true },
    );
  };

  const updateMember = (index: number, patch: Partial<(typeof members)[number]>) => {
    const updated = [...members];
    updated[index] = { ...updated[index], ...patch };
    setValue("members", updated, { shouldDirty: true });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/destination-teams"
        isNew={isNew}
        newTitle={t("catalog.teams.edit.newTitle")}
        editTitle={t("catalog.teams.edit.editTitle")}
        entityName={itemData?.name}
        formId="team-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        validationErrors={errors}
        isSuccess={saveMutation.isSuccess}
      />

      <form id="team-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.basicInfo")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="label">
                  {t("common.fields.name")} *
                </label>
                <input
                  {...register("name")}
                  type="text"
                  id="name"
                  className={`input ${errors.name ? "input-error" : ""}`}
                  placeholder={t("catalog.teams.edit.namePlaceholder")}
                />
                {errors.name && (
                  <p className="text-error-600 mt-1 text-sm">{errors.name.message}</p>
                )}
              </div>

              {/* Typ sekce karty jen u karty bez destinací; jinak ho určuje role první
                  destinace. RHF hodnotu skrytého pole drží (`shouldUnregister` je false). */}
              {links.length === 0 && (
                <div>
                  <label htmlFor="sectionType" className="label">
                    {t("catalog.teams.edit.sectionType")} *
                  </label>
                  <select {...register("sectionType")} id="sectionType" className="input">
                    {TEAM_SECTION_TYPES.map((sectionType) => (
                      <option key={sectionType} value={sectionType}>
                        {SECTION_LABELS[sectionType] || sectionType}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-3 sm:col-span-2">
                <label htmlFor="team-destinations" className="label">
                  {t("catalog.teams.edit.destinationsLabel")}
                </label>
                <Controller
                  name="destinationLinks"
                  control={control}
                  render={({ field }) => (
                    <DestinationPicker
                      id="team-destinations"
                      ref={field.ref}
                      mode="multiple"
                      level="all"
                      value={links.map((l) => l.destinationId)}
                      onChange={(ids) =>
                        applyLinks(
                          linksForIds(links, ids, {
                            sectionType: cardSectionType,
                            sortOrder: cardSortOrder,
                          }),
                        )
                      }
                    />
                  )}
                />
                <TeamDestinationRows
                  links={links}
                  destinationName={destinationName}
                  roleLabels={SECTION_LABELS}
                  cardPhone={cardPhone}
                  others={others}
                  onRowChange={onRowChange}
                />
              </div>

              <div className="sm:col-span-2">
                <Controller
                  name="guideId"
                  control={control}
                  render={({ field }) => (
                    <GuideProfileField
                      id="guideId"
                      value={field.value}
                      guides={guideLookup.data?.data}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>

              <div>
                <label htmlFor="subtitle" className="label">
                  {t("catalog.teams.edit.subtitleRole")}
                </label>
                <input
                  {...register("subtitle")}
                  type="text"
                  id="subtitle"
                  className="input"
                  placeholder={t("catalog.teams.edit.subtitlePlaceholder")}
                />
              </div>

              <div>
                <label htmlFor="email" className="label">
                  {t("common.fields.email")}
                </label>
                <input
                  {...register("email")}
                  type="email"
                  id="email"
                  className={`input ${errors.email ? "input-error" : ""}`}
                  placeholder={t("catalog.teams.edit.emailPlaceholder")}
                />
                {errors.email && (
                  <p className="text-error-600 mt-1 text-sm">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="label">
                  {t("common.fields.phone")}
                </label>
                <input
                  {...register("phone")}
                  type="text"
                  id="phone"
                  className="input"
                  placeholder={t("catalog.teams.edit.phonePlaceholder")}
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.teams.edit.bioTitle")}
            </h2>
            <textarea
              {...register("bio")}
              id="bio"
              rows={5}
              className="input"
              placeholder={t("catalog.teams.edit.bioPlaceholder")}
            />
          </div>

          {/* Členové karty — vykreslí se na webu u KAŽDÉHO typu sekce, který je má. */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("catalog.teams.edit.membersTitle")}
              </h2>
              <button type="button" onClick={addMember} className="btn-ghost btn-sm">
                <Plus className="h-4 w-4" />
                {t("catalog.teams.edit.addMember")}
              </button>
            </div>
            <p className="mb-4 text-sm text-gray-500">{t("catalog.teams.edit.membersHint")}</p>
            {members.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                {t("catalog.teams.edit.membersEmpty")}
              </p>
            ) : (
              <div className="space-y-4">
                {members.map((member, index) => (
                  <div
                    key={index}
                    className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateMember(index, { name: e.target.value })}
                        className={`input flex-1 ${errors.members?.[index]?.name ? "input-error" : ""}`}
                        placeholder={t("common.fields.name")}
                      />
                      <input
                        type="text"
                        value={member.role || ""}
                        onChange={(e) => updateMember(index, { role: e.target.value })}
                        className="input w-48"
                        placeholder={t("catalog.teams.edit.rolePlaceholder")}
                      />
                      <button
                        title={t("common.actions.remove")}
                        type="button"
                        onClick={() => removeMember(index)}
                        className="btn-ghost btn-sm text-error-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {/* Vlastní kontakt člena (F2.4) — ukáže se U NĚJ, ne u sekce. */}
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        type="text"
                        value={member.phone || ""}
                        onChange={(e) => updateMember(index, { phone: e.target.value })}
                        className="input flex-1"
                        placeholder={t("catalog.teams.edit.memberPhonePlaceholder")}
                      />
                      <input
                        type="email"
                        value={member.email || ""}
                        onChange={(e) => updateMember(index, { email: e.target.value })}
                        className={`input flex-1 ${errors.members?.[index]?.email ? "input-error" : ""}`}
                        placeholder={t("catalog.teams.edit.memberEmailPlaceholder")}
                      />
                    </div>
                    {errors.members?.[index]?.email && (
                      <p className="text-error-600 text-sm">
                        {errors.members[index]?.email?.message}
                      </p>
                    )}
                    {errors.members?.[index]?.name && (
                      <p className="text-error-600 text-sm">
                        {errors.members[index]?.name?.message}
                      </p>
                    )}
                    <div>
                      <label className="label">{t("catalog.photo")}</label>
                      <MediaPicker
                        mode="single"
                        value={member.photoId ?? null}
                        onChange={(photoId) => updateMember(index, { photoId })}
                        folder="teams"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Photo Gallery */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.teams.edit.galleryTitle")}
            </h2>
            <Controller
              name="photoIds"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="multiple"
                  value={field.value || []}
                  onChange={field.onChange}
                  folder="teams"
                  label={t("catalog.teams.edit.uploadTeamPhotos")}
                />
              )}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.teams.edit.mainPhoto")}
            </h2>
            <Controller
              name="photoMediaId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="single"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  folder="teams"
                />
              )}
            />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("common.settings")}
            </h2>
            <div className="space-y-4">
              {/* Pořadí karty jen u karty bez destinací; jinak pořadí první destinace. */}
              {links.length === 0 && (
                <div>
                  <label htmlFor="sortOrder" className="label">
                    {t("common.fields.sortOrder")}
                  </label>
                  <input
                    {...register("sortOrder", { valueAsNumber: true })}
                    type="number"
                    id="sortOrder"
                    className="input"
                    min={0}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t("catalog.teams.edit.sortOrderHint")}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  {...register("isActive")}
                  type="checkbox"
                  id="isActive"
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t("catalog.teams.edit.activeVisible")}
                </label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
